<template>
  <div class="tender-opportunities">
    <div class="tender-opportunities__header">
      <div class="title">{{ $t('menu.opportunities') }}</div>
    </div>
    <div v-if="pending">
      <BaseCircularLoadingBar :size="50" :text="$t('page.opportunity.loading-document-text')" />
    </div>
    <template v-else>
      <div
        v-for="(opportunity, index) in tenderOpportunities"
        :key="index"
        class="tender-opportunities__opportunity-box"
        v-tooltip="{ content: $t('actions.consult') }"
      >
        <div class="tender-opportunities__opportunity-consult" @click="goToOpportunity(opportunity)">
          <div class="tender-opportunities__opportunity-header">
            <div class="tender-opportunities__opportunity-subtitle">
              <BaseIcon class="tender-opportunities__opportunity-icon" name="bullseye-arrow" />
              {{ $t('page.opportunity.opportunity') }}
            </div>
            <div class="tender-opportunities__opportunity-title">
              <TextHighlight :content="opportunity.name" isPlain></TextHighlight>
            </div>
          </div>
        </div>
        <div class="tender-opportunities__opportunity-content" v-if="tenantHasDocumentsAccess()">
          <OpportunityDocuments :opportunity="opportunity" />
        </div>
      </div>
      <FileUpload
        v-if="tenantHasDocumentsAccess() && !tenderOpportunities.length"
        multiple
        icon="bullseye-arrow"
        :title="$tc('actions.tender.convert-to-opportunity')"
        @add-files="createNewOppWithDocuments"
      />
      <div v-else-if="tenderOpportunities.length === 0" class="tender-opportunities__no-opportunities">
        {{ $t('tender-details.opportunities.no-opportunities') }}
      </div>
    </template>
  </div>
</template>

<script>
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import FileUpload from '@/components/form/FileUpload';
import OpportunityDocuments from '@/components/page-opportunity-details/OpportunityDocuments';
import TextHighlight from '@/components/text-highlight/TextHighlight';

import { isXlsFile } from '@/util/document-file-util';
import { mapActions, mapGetters } from 'vuex';
import { tenantHasSlipExtractionAccess, tenantHasDocumentsAccess } from '@/util/module-access-util';
import { removeMarks } from '@/util/mark-util';

export default {
  name: 'TenderOpportunities',
  components: {
    BaseCircularLoadingBar,
    BaseIcon,
    FileUpload,
    OpportunityDocuments,
    TextHighlight,
  },
  props: {
    tender: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('tenderOpportunities', ['tenderOpportunities', 'pending']),
  },
  methods: {
    ...mapActions('opportunityDocuments', ['addDocuments']),
    ...mapActions('tenderOpportunities', ['addOpportunity']),
    tenantHasDocumentsAccess,
    tenantHasSlipExtractionAccess,
    goToOpportunity(opportunity) {
      if (!opportunity) return;
      this.$router.push({
        name: 'opportunity-details',
        params: { opportunityId: opportunity.id },
      });
    },
    async createNewOppWithDocuments(files) {
      const filesArray = Array.from(files);
      const containsForbiddenFormat = filesArray.some((file) => isXlsFile(file) && !tenantHasSlipExtractionAccess());
      if (containsForbiddenFormat) {
        this.$toast.warning(this.$t('page.opportunity-download.wrong-format'));
        return;
      }

      try {
        const oppName = removeMarks(this.tender.title);
        const returnedOpportunity = await this.addOpportunity({ name: oppName, tenderId: this.tender.id });
        this.uploadDocumentsToServer(files, returnedOpportunity);
      } catch (error) {
        this.$toast.error(this.$t('page.opportunity-details.opportunity-manipulations.add-fail'));
      }
    },
    uploadDocumentsToServer(files, opportunity) {
      let formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        let file = files[i];
        if (file) formData.append('file[]', file);
      }

      this.addDocuments({
        opportunity: opportunity,
        formData,
      })
        .then(() => {
          this.$toast.success(this.$t('page.opportunity-details.document-manipulations.add-success'));
          // TODO: check if tenant has llm extraction module before displaying the toast
          this.$toast.success(this.$t('page.opportunity-details.document-manipulations.llm-extraction-scheduled'));
        })
        .catch((error) => {
          this.$toast.error(
            error,
            {},
            this.$t('page.opportunity-details.document-manipulations.add-fail', { file: error.message }),
          );
        });
    },
  },
  // Opportunities are loaded by PageTenderDetails along with the tender.
};
</script>

<style lang="scss">
.tender-opportunities {
  padding: 2rem;
  background: $color-white;
  border-radius: $border-radius-root;
  margin-bottom: 1rem;

  &__header {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 16px;
  }

  &__opportunity-box {
    border: 1px solid $color-border;
    border-radius: $border-radius-medium;
    margin-bottom: 8px;
    transition: all 200ms ease;
    cursor: pointer;

    &:hover {
      border: 1px solid $color-bluey-gray;
      transition: all 200ms ease;

      .tender-opportunities__opportunity-consult {
        background: $color-hover;
        transition: all 200ms ease;
      }
    }
  }

  &__opportunity-title  {
    display: flex;
    flex-direction: row;
  }

  &__opportunity-consult {
    justify-content: space-between;
    align-items: center;
    display: flex;
    padding: 1rem;
    background: $color-neutral-pale;
    width: 100%;
    transition: all 200ms ease;
    margin-bottom: 3px;
    border-radius: $border-radius-medium $border-radius-medium 0 0;
  }

  &__opportunity-content {
    padding: 1rem;
  }

  &__opportunity-subtitle {
    font-size: 14px;
    align-items: center;
    display: flex;
    gap: 4px;
    color: $color-bluey-gray;
    padding-bottom: 4px;
  }

  &__opportunity-icon {
    width: 16px;
    height: 16px;
    color: $color-bluey-gray;
  }

  &__opportunity-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__opportunity-header {
    display: flex;
    flex-direction: column;
  }

  &__no-opportunities {
    color: $color-neutral;
    font-size: $font-size-small;
    line-height: 1.43;
    list-style-type: none;
    word-break: break-word;
  }
}
</style>
